<script setup lang="ts">
type Tool = 'face-swap' | 'sex-pose' | 'ai-video' | 'seedance' | 'text-to-image' | 'image-to-image';

defineProps<{ tool: Tool }>();
const emit = defineEmits<{ (e: 'switch', t: Tool): void }>();
const { t } = useI18n();
const localePath = useLocalePath();

interface NavItem { id: Tool | string; label: string; icon: string; isNew?: boolean; isHot?: boolean; href?: string }

const NAV = computed<NavItem[]>(() => [
  { id: 'face-swap', label: t('Face Swap'), icon: 'mdi-face-man-shimmer' },
  { id: 'sex-pose',  label: t('AI Pose'),   icon: 'mdi-human' },
  { id: 'ai-video',  label: t('AI Video'),  icon: 'mdi-video', isHot: true },
  { id: 'seedance',  label: 'AI Studio',    icon: 'mdi-movie-open-star', isNew: true },
  { id: 'i2v',       label: 'Image to Video', icon: 'mdi-movie-filter', isNew: true, href: localePath('/image-to-video') },
  { id: 'text-to-image',  label: t('imageGen.textToImage'),  icon: 'mdi-image-plus' },
  { id: 'image-to-image', label: t('imageGen.imageToImage'), icon: 'mdi-image-edit' },
]);
</script>

<template>
  <aside class="side">
    <template v-for="n in NAV" :key="n.id">
      <NuxtLink
        v-if="n.href"
        :to="n.href"
        class="nav-btn"
        :class="{ 'i2v-btn': n.id === 'i2v' }"
      >
        <v-icon :icon="n.icon" size="18" />
        <span>{{ n.label }}</span>
        <span v-if="n.isNew" class="new-badge">NEW</span>
      </NuxtLink>
      <button
        v-else
        class="nav-btn"
        :class="{ active: tool === n.id, 'seedance-btn': n.id === 'seedance' }"
        @click="emit('switch', n.id as any)"
      >
        <v-icon :icon="n.icon" size="18" />
        <span>{{ n.label }}</span>
        <span v-if="n.isHot" class="hot-badge">HOT</span>
        <span v-if="n.isNew" class="new-badge">NEW</span>
      </button>
    </template>
  </aside>
</template>

<style scoped lang="scss">
/* 移动端：胶囊标签按内容宽度排布，一行横向滚动（隐藏滚动条），不再等宽挤压；
   桌面端(md+)：恢复原竖向侧栏 */
.side {
  @apply flex flex-row md:flex-col gap-2 md:gap-3 w-full
         overflow-x-auto md:overflow-visible;
  /* 移动端横向滚动预留左右边距，避免首尾贴边 */
  scroll-padding: 0 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
.nav-btn {
  /* 未选中：参照 face-swap 顶部胶囊（bg-neutral-900 + rounded-full）；
     选中：白底黑字 */
  /* 移动端按内容宽度(flex-none)胶囊，横向滚动；桌面端铺满侧栏 */
  @apply flex flex-none items-center justify-center md:justify-start
         gap-1.5 md:gap-3 rounded-full px-3 md:px-4 py-2.5 md:py-3
         text-sm font-semibold bg-neutral-900 text-white/70
         hover:text-white hover:bg-neutral-800
         transition whitespace-nowrap;
  min-width: 0;
  &.active {
    @apply bg-white text-black hover:bg-white;
  }
  :deep(.v-icon) { @apply opacity-80; }
  &.active :deep(.v-icon) { @apply opacity-100; }
}
.seedance-btn {
  @apply relative text-white;
  background: linear-gradient(90deg, rgba(217, 70, 239, 0.15), rgba(244, 63, 94, 0.15));
  border: 1px solid rgba(217, 70, 239, 0.3);
  text-decoration: none;
  &:hover {
    background: linear-gradient(90deg, rgba(217, 70, 239, 0.25), rgba(244, 63, 94, 0.25));
    color: white;
  }
  :deep(.v-icon) {
    color: #e879f9;
    opacity: 1;
  }
}
.i2v-btn {
  @apply relative text-white;
  background: linear-gradient(90deg, rgba(217, 70, 239, 0.15), rgba(244, 63, 94, 0.15));
  border: 1px solid rgba(217, 70, 239, 0.3);
  text-decoration: none;
  &:hover {
    background: linear-gradient(90deg, rgba(217, 70, 239, 0.25), rgba(244, 63, 94, 0.25));
    color: white;
  }
  :deep(.v-icon) {
    color: #e879f9;
    opacity: 1;
  }
}
.new-badge {
  /* 移动端内容宽胶囊内 badge 紧跟文字；桌面端(md+)推到右侧 */
  @apply md:ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white leading-none;
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  box-shadow: 0 2px 8px rgba(217, 70, 239, 0.35);
}
.hot-badge {
  @apply md:ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white leading-none;
  background: #ef4444;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35);
}
</style>
